import {resource} from '@albert-io/json-api-framework/request/builder';
import {maybeApplyFilterMetaContext} from 'client/components/ContentDiscovery/ContentDiscovery.utils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';

export const getBaseQuestionTypeCountQuery = () => {
  const query = resource('question_sets_v1')
    .mandarkEndpoint(['question_sets_v1'])
    .filter({
      subject_v2: {
        hidden: false
      }
    })
    .filter({
      questions_v3: {
        status: 'published'
      }
    })
    .meta({
      context: {
        question_type_counts: true
      }
    })
    .withMeta('question_set_v1');

  return query;
};

export const getBaseQuestionDifficultyCountQuery = () => {
  const query = resource('question_sets_v1')
    .mandarkEndpoint(['question_sets_v1'])
    .filter({
      subject_v2: {
        hidden: false
      }
    })
    .filter({
      questions_v3: {
        status: 'published'
      }
    })
    .meta({
      context: {
        question_difficulty_counts: true
      }
    })
    .withMeta('question_set_v1');

  return query;
};

export const getStandardSetQuestionCountQuery = (
  standardSetId,
  questionDifficulties,
  questionTypes,
  hideAssessment,
  hidePreviouslyAssigned,
  guideLevels,
  subjects,
  standardIds
) => {
  let query = resource('standard_sets_v1')
    .mandarkEndpoint(['standard_sets_v1', standardSetId])
    .meta({
      context: {
        content_discovery_filtering: true,
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .withMeta('standard_set_v1');

  query = maybeApplyFilterMetaContext(query, {
    questionDifficulties,
    questionTypes,
    hideAssessment,
    hidePreviouslyAssigned,
    guideLevels,
    subjects,
    standardIds
  });

  return query;
};

export const getStandardSetsQuestionCountQuery = (
  standardSetIds,
  questionDifficulties,
  questionTypes,
  hideAssessment,
  hidePreviouslyAssigned,
  guideLevels,
  subjects
) => {
  let query = resource('standard_sets_v1')
    .mandarkEndpoint(['standard_sets_v1'])
    .filter({
      id: standardSetIds
    })
    .meta({
      context: {
        content_discovery_filtering: true,
        question_count: true,
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .withMeta('standard_set_v1');

  query = maybeApplyFilterMetaContext(query, {
    questionDifficulties,
    questionTypes,
    hideAssessment,
    hidePreviouslyAssigned,
    guideLevels,
    subjects
  });

  return query;
};

export const getStandardQuestionCountsQuery = (
  questionDifficulties,
  questionTypes,
  hideAssessment,
  hidePreviouslyAssigned,
  guideLevels,
  subjects,
  standardIds
) => {
  let query = resource('standards_v1')
    .mandarkEndpoint(['standards_v1'])
    .filter({
      id: standardIds.join()
    })
    .meta({
      context: {
        content_discovery_filtering: true,
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .withMeta('standard_v1');

  query = maybeApplyFilterMetaContext(query, {
    questionDifficulties,
    questionTypes,
    hideAssessment,
    hidePreviouslyAssigned,
    guideLevels,
    subjects
  });

  return query;
};

export const getStandardSetStandardsQuestionCountQuery = (
  standardSetId,
  questionDifficulties,
  questionTypes,
  hideAssessment,
  hidePreviouslyAssigned,
  guideLevels,
  subjects
) => {
  let query = resource('standard_sets_v1')
    .mandarkEndpoint(['standard_sets_v1', standardSetId])
    .include('standards_v1')
    .meta({
      context: {
        content_discovery_filtering: true,
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .withMeta('standard_set_v1,standard_v1');

  query = maybeApplyFilterMetaContext(query, {
    questionDifficulties,
    questionTypes,
    hideAssessment,
    hidePreviouslyAssigned,
    guideLevels,
    subjects
  });

  return query;
};

export const getFilteredStandardSetStandardsQuestionCountQuery = (
  standardSetId,
  questionDifficulties,
  questionTypes,
  hideAssessment,
  hidePreviouslyAssigned,
  guideLevels,
  subjects,
  selectionState?,
  filteredStandardIds?
) => {
  let query = resource('standard_sets_v1')
    .mandarkEndpoint(['standard_sets_v1', standardSetId])
    .include('standards_v1')
    .meta({
      context: {
        content_discovery_filtering: true,
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .withMeta('standard_set_v1,standard_v1');

  if (selectionState === 'selected' && filteredStandardIds) {
    query = query.filter({
      included: {
        standards_v1: {
          id: filteredStandardIds
        }
      }
    });
  } else if (selectionState === 'deselected' && filteredStandardIds) {
    query = query.filter({
      included: {
        standards_v1: {
          id: {not: filteredStandardIds}
        }
      }
    });
  }

  query = maybeApplyFilterMetaContext(query, {
    questionDifficulties,
    questionTypes,
    hideAssessment,
    hidePreviouslyAssigned,
    guideLevels,
    subjects
  });

  return query;
};

export const getGuideLevelsQuestionCountQuery = (
  guideLevelIds,
  questionDifficulties,
  questionTypes,
  standardIds,
  standardSetIds,
  hideAssessment,
  hidePreviouslyAssigned
) => {
  let query = resource('guide_level_v2')
    .mandarkEndpoint(['guide_levels_v2'])
    .filter({
      id: guideLevelIds
    })
    .meta({
      context: {
        content_discovery_filtering: true,
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .withMeta('guide_level_v2');

  query = maybeApplyFilterMetaContext(query, {
    questionDifficulties,
    questionTypes,
    standardIds,
    standardSetIds,
    hideAssessment,
    hidePreviouslyAssigned
  });

  return query;
};

export const getGuideLevelQuestionCountQuery = (
  guideLevelId,
  questionDifficulties,
  questionTypes,
  standardIds,
  standardSetIds,
  hideAssessment,
  hidePreviouslyAssigned
) => {
  let query = resource('guide_level_v2')
    .mandarkEndpoint(['guide_levels_v2', guideLevelId])
    .meta({
      context: {
        content_discovery_filtering: true,
        user: {
          id: masqueradeStore.getUserIdByMasqueradeState()
        }
      }
    })
    .withMeta('guide_level_v2');

  query = maybeApplyFilterMetaContext(query, {
    questionDifficulties,
    questionTypes,
    standardIds,
    standardSetIds,
    hideAssessment,
    hidePreviouslyAssigned
  });

  return query;
};
